/**
 * 単元冒頭の「解説パート」= スクリプト駆動アニメのシナリオ(docs/13 §8)。
 *
 * 動画ファイルではなく**コマの列**でつくる。1本ぶんが数KBで済み、直しは1行、
 * 多言語化はセリフと音声の差し替えだけ、管理画面でレビューもできる
 * (パズルの盤面アニメ解説 = `packages/puzzle` の explain 系の一般化)。
 *
 * コマ = {話者, セリフ(かな。ふりがな対応), 表情/ポーズ, 盤面アクション(任意), 音声(任意)}。
 * 話者は docs/14 の3人 + ナレーション:
 *   - `pal`      パル(生徒目線。口ぐせは「やった! またひとつ せいちょうしたよ!」docs/14 §2)
 *   - `octo`     オクト先生(解説役)
 *   - `poka`     ポカ(**わざと間違える役**。視聴者が心の中で訂正 → オクト先生が種明かし)
 *   - `narrator` 語り(キャラの絵を出さない地の文)
 *
 * 盤面は**絵の入れ物**で、位置だけを持つ「スロット」の集まり。
 * アクションはそこに数字・絵文字を入れたり、光らせたり、矢印を引いたり、
 * まとめて囲んだりする。さくらんぼ計算(10のまとまり)が書ける最小セットにしてある。
 *
 * このファイルは**描画非依存の純TS**(CLAUDE.md ルール1)。座標は
 * `QUIZ_LESSON.boardWidth` × `QUIZ_LESSON.boardHeight` の抽象座標で、px ではない。
 */

import { QUIZ_LESSON } from './config.js';
import { parseFractionRuns, type FractionRun } from './fraction.js';
import {
  QUIZ_ID_PATTERN,
  QUIZ_KANJI,
  QUIZ_RUBY,
  isQuizAssetPath,
  isRecord,
  isText,
} from './text.js';
import { allowsKanjiPrompt, type QuizBand } from './types.js';

// ── 話者・表情・ポーズ ────────────────────────────────────

/** 話者(docs/14 の3人 + 語り)。 */
export type LessonSpeaker = 'pal' | 'octo' | 'poka' | 'narrator';

/** すべての話者。 */
export const LESSON_SPEAKERS: readonly LessonSpeaker[] = ['pal', 'octo', 'poka', 'narrator'];

/** 絵が出る話者(`narrator` だけは地の文なので絵を持たない)。 */
export const LESSON_CHARACTERS: readonly LessonSpeaker[] = ['pal', 'octo', 'poka'];

/**
 * 表情。3種だけに絞る(プレースホルダ絵でも本番アートでも同じ3種で成立させ、
 * 差し替え時にシナリオを書き直さなくてよいようにする)。
 */
export type LessonMood = 'normal' | 'happy' | 'puzzled';

/** すべての表情。 */
export const LESSON_MOODS: readonly LessonMood[] = ['normal', 'happy', 'puzzled'];

/** ポーズ。いまは左右の向きだけ(反転)。 */
export type LessonPose = 'left' | 'right';

/** すべてのポーズ。 */
export const LESSON_POSES: readonly LessonPose[] = ['left', 'right'];

// ── 盤面 ──────────────────────────────────────────────────

/** スロットの見た目。`box` = しかくい ます / `circle` = まる / `plain` = わくなし(記号)。 */
export type LessonSlotShape = 'box' | 'circle' | 'plain';

/** すべての見た目。 */
export const LESSON_SLOT_SHAPES: readonly LessonSlotShape[] = ['box', 'circle', 'plain'];

/** 盤面のスロット1つ(数字・絵文字の入れ物)。 */
export interface LessonSlot {
  /** スロットID(盤面の中で一意)。アクションはこれで指す。 */
  readonly id: string;
  /** 中心の位置(0 〜 `QUIZ_LESSON.boardWidth` / `boardHeight`)。 */
  readonly x: number;
  readonly y: number;
  /** 大きさ(省略時は `QUIZ_LESSON.slotWidth` / `slotHeight`)。 */
  readonly w?: number;
  readonly h?: number;
  readonly shape?: LessonSlotShape;
  /**
   * 最初から出ている文字(「+」「=」のような記号)。
   * `place` で入る数字と違い、`clear` でも消えない**盤面の地の文**。
   */
  readonly text?: string;
}

/** 盤面。 */
export interface LessonBoard {
  readonly slots: readonly LessonSlot[];
}

// ── 盤面アクション ────────────────────────────────────────

/**
 * 盤面アクションの種類(さくらんぼ計算が書ける最小セット)。
 *
 * `place`     … スロットに数字・絵文字が**ポンと入る**
 * `highlight` … スロットを光らせる(いま話している場所を指す)
 * `arrow`     … スロットからスロットへ矢印(さくらんぼの えだ・移動)
 * `group`     … 複数スロットを**まとめて囲む**(10のまとまり)
 * `clear`     … 入った数字を消す(`slots` 省略で全部。まちがいの取り消し)
 *
 * **持続のしかた**(描画側と共有する約束):
 *   - `place` / `clear` は**その後のコマにも残る**(盤面の状態)
 *   - `highlight` / `arrow` / `group` は**そのコマの間だけ**(演出)
 */
export type LessonActionKind = 'place' | 'highlight' | 'arrow' | 'group' | 'clear';

/** すべてのアクション。 */
export const LESSON_ACTION_KINDS: readonly LessonActionKind[] = [
  'place',
  'highlight',
  'arrow',
  'group',
  'clear',
];

/** 盤面アクション1つ。使うフィールドは `kind` で決まる。 */
export interface LessonAction {
  readonly kind: LessonActionKind;
  /** `place`: 入れ先のスロットID。 */
  readonly slot?: string;
  /** `place`: 入れる文字(数字・絵文字)。 */
  readonly value?: string;
  /** `highlight` / `group` / `clear`: 対象のスロットID。 */
  readonly slots?: readonly string[];
  /** `arrow`: 矢印の元。 */
  readonly from?: string;
  /** `arrow`: 矢印の先。 */
  readonly to?: string;
  /** `arrow` / `group`: そえがき(かな。省略可)。 */
  readonly label?: string;
}

// ── コマ ──────────────────────────────────────────────────

/** コマ1つ。 */
export interface LessonFrame {
  readonly speaker: LessonSpeaker;
  /** セリフ(ひらがな優先。漢字を使うなら `ruby` が必須)。 */
  readonly text: string;
  /** ふりがな(かなのみ)。 */
  readonly ruby?: string;
  /** 表情(既定 `normal`)。`narrator` には付けられない。 */
  readonly mood?: LessonMood;
  /** 向き(既定 `left`)。`narrator` には付けられない。 */
  readonly pose?: LessonPose;
  /** 盤面アクション(省略可)。 */
  readonly actions?: readonly LessonAction[];
  /**
   * 読み上げ音声の相対パス(`au/ja/…`)。
   * **いまは常に無し**(新規TTS生成は保留中。docs/13 §8)。フックだけ先に開けてある。
   */
  readonly audio?: string;
}

/** 解説パート1本(= 単元1つぶん)。 */
export interface QuizLesson {
  /** 解説ID。**単元IDと同じ**にする(1単元に1本)。 */
  readonly id: string;
  /** 見出し(ひらがな。例「さくらんぼで 10を つくろう」)。 */
  readonly title: string;
  readonly board: LessonBoard;
  readonly frames: readonly LessonFrame[];
}

// ── 検証 ──────────────────────────────────────────────────

/** 盤面座標として妥当か(有限の数で、盤面の中に収まる)。 */
function checkCoord(value: unknown, max: number, where: string, field: string, errors: string[]) {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    errors.push(`${where}: ${field} は数値が必要です`);
    return;
  }
  if (value < 0 || value > max) {
    errors.push(`${where}: ${field} は 0〜${max} の範囲です(${value})`);
  }
}

/** 盤面を検証して、スロットIDの集合を返す(壊れていれば空)。 */
function validateBoard(value: unknown, where: string, errors: string[]): Set<string> {
  const ids = new Set<string>();
  if (!isRecord(value)) {
    errors.push(`${where}: board はオブジェクトが必要です`);
    return ids;
  }
  const slots = value.slots;
  if (!Array.isArray(slots) || slots.length === 0) {
    errors.push(`${where}: board.slots が空です`);
    return ids;
  }
  if (slots.length > QUIZ_LESSON.maxSlots) {
    errors.push(`${where}: board.slots は${QUIZ_LESSON.maxSlots}個までです(${slots.length}個)`);
  }

  for (const [i, slot] of slots.entries()) {
    const at = `${where}.board.slots[${i}]`;
    if (!isRecord(slot)) {
      errors.push(`${at}: オブジェクトではありません`);
      continue;
    }
    if (!isText(slot.id)) errors.push(`${at}: id が空です`);
    else if (!QUIZ_ID_PATTERN.test(slot.id)) {
      errors.push(`${at}: id ${slot.id} は英小文字+数字+ハイフンが必要です`);
    } else if (ids.has(slot.id)) errors.push(`${at}: スロットID ${slot.id} が重複しています`);
    else ids.add(slot.id);

    checkCoord(slot.x, QUIZ_LESSON.boardWidth, at, 'x', errors);
    checkCoord(slot.y, QUIZ_LESSON.boardHeight, at, 'y', errors);
    if (slot.w !== undefined) checkCoord(slot.w, QUIZ_LESSON.boardWidth, at, 'w', errors);
    if (slot.h !== undefined) checkCoord(slot.h, QUIZ_LESSON.boardHeight, at, 'h', errors);

    if (slot.shape !== undefined && !LESSON_SLOT_SHAPES.includes(slot.shape as LessonSlotShape)) {
      errors.push(`${at}: 未知の shape ${String(slot.shape)}`);
    }
    // 盤面の地の文は「+」「=」のような記号。漢字は読めない子がいるので入れさせない。
    if (slot.text !== undefined) {
      if (!isText(slot.text)) errors.push(`${at}: text が空です`);
      else if (QUIZ_KANJI.test(slot.text)) errors.push(`${at}: text に漢字は使えません`);
    }
  }
  return ids;
}

/** スロット参照が盤面にあるか。 */
function checkSlotRef(
  value: unknown,
  slots: ReadonlySet<string>,
  where: string,
  field: string,
  errors: string[],
): void {
  if (!isText(value)) {
    errors.push(`${where}: ${field} が空です`);
    return;
  }
  if (!slots.has(value)) errors.push(`${where}: ${field} ${value} は board にありません`);
}

/** 盤面アクション1つを検証する。 */
function validateAction(
  value: unknown,
  slots: ReadonlySet<string>,
  where: string,
  errors: string[],
): void {
  if (!isRecord(value)) {
    errors.push(`${where}: オブジェクトではありません`);
    return;
  }
  const { kind, slot, value: placed, slots: targets, from, to, label } = value;
  if (!LESSON_ACTION_KINDS.includes(kind as LessonActionKind)) {
    errors.push(`${where}: 未知の kind ${String(kind)}(有効: ${LESSON_ACTION_KINDS.join(' / ')})`);
    return;
  }

  /** `slots` を見る(空配列・重複・未知のIDを弾く)。`required` なら省略も弾く。 */
  const checkTargets = (required: boolean): void => {
    if (targets === undefined) {
      if (required) errors.push(`${where}: ${String(kind)} には slots が必要です`);
      return;
    }
    if (!Array.isArray(targets) || targets.length === 0) {
      errors.push(`${where}: slots が空です`);
      return;
    }
    const seen = new Set<string>();
    for (const [i, id] of targets.entries()) {
      checkSlotRef(id, slots, where, `slots[${i}]`, errors);
      if (typeof id === 'string') {
        if (seen.has(id)) errors.push(`${where}: slots に ${id} が2回あります`);
        seen.add(id);
      }
    }
  };

  switch (kind) {
    case 'place':
      checkSlotRef(slot, slots, where, 'slot', errors);
      if (!isText(placed)) errors.push(`${where}: place には value(入れる文字)が必要です`);
      else if (QUIZ_KANJI.test(placed)) errors.push(`${where}: value に漢字は使えません`);
      break;
    case 'highlight':
      checkTargets(true);
      break;
    case 'group':
      checkTargets(true);
      // 1つだけを「まとめて囲む」のは highlight の仕事。取り違えを形の時点で弾く。
      if (Array.isArray(targets) && targets.length < 2) {
        errors.push(`${where}: group は2つ以上の slots を囲みます(1つなら highlight)`);
      }
      break;
    case 'arrow':
      checkSlotRef(from, slots, where, 'from', errors);
      checkSlotRef(to, slots, where, 'to', errors);
      if (isText(from) && from === to) errors.push(`${where}: arrow の from と to が同じです`);
      break;
    default: // clear
      checkTargets(false);
      break;
  }

  if (label !== undefined) {
    if (!isText(label)) errors.push(`${where}: label が空です`);
    else if (QUIZ_KANJI.test(label)) errors.push(`${where}: label に漢字は使えません`);
    else if (kind !== 'arrow' && kind !== 'group') {
      errors.push(`${where}: label は arrow / group だけに付けられます`);
    }
  }
}

/** コマ1つを検証する。 */
function validateFrame(
  value: unknown,
  slots: ReadonlySet<string>,
  band: QuizBand | undefined,
  where: string,
  errors: string[],
): void {
  if (!isRecord(value)) {
    errors.push(`${where}: オブジェクトではありません`);
    return;
  }
  const { speaker, text, ruby, mood, pose, actions, audio } = value;

  const knownSpeaker = LESSON_SPEAKERS.includes(speaker as LessonSpeaker);
  if (!knownSpeaker) {
    errors.push(
      `${where}: 未知の speaker ${String(speaker)}(有効: ${LESSON_SPEAKERS.join(' / ')})`,
    );
  }

  // セリフの漢字は問題文と同じ扱い(帯で可否が変わり、使うならふりがな必須)。
  // 帯が分からないときは「まだ読めない子」と同じ、いちばん厳しい方に倒す。
  const kanjiOk = band !== undefined && allowsKanjiPrompt(band);
  if (!isText(text)) {
    errors.push(`${where}: text が空です`);
  } else {
    if (text !== text.trim()) errors.push(`${where}: text の前後に空白があります`);
    if (text.length > QUIZ_LESSON.maxTextLength) {
      errors.push(
        `${where}: text は${QUIZ_LESSON.maxTextLength}文字までです(${text.length}文字)。` +
          `コマを分けてください`,
      );
    }
    if (QUIZ_KANJI.test(text)) {
      if (!kanjiOk) {
        errors.push(`${where}: ${String(band)} の text に漢字は使えません: ${text}`);
      } else if (ruby === undefined) {
        errors.push(`${where}: text に漢字があるので ruby(ふりがな)が必要です`);
      }
    }
  }

  if (ruby !== undefined) {
    if (!isText(ruby)) errors.push(`${where}: ruby が空です`);
    else if (!QUIZ_RUBY.test(ruby)) {
      errors.push(`${where}: ruby はかな(と数字・約物)だけで書いてください: ${ruby}`);
    }
  }

  // 語りは絵を出さないので、表情・ポーズを書いても行き先が無い(書き間違いのサイン)。
  const narrator = speaker === 'narrator';
  if (mood !== undefined) {
    if (!LESSON_MOODS.includes(mood as LessonMood)) {
      errors.push(`${where}: 未知の mood ${String(mood)}(有効: ${LESSON_MOODS.join(' / ')})`);
    } else if (narrator) errors.push(`${where}: narrator に mood は付けられません`);
  }
  if (pose !== undefined) {
    if (!LESSON_POSES.includes(pose as LessonPose)) {
      errors.push(`${where}: 未知の pose ${String(pose)}(有効: ${LESSON_POSES.join(' / ')})`);
    } else if (narrator) errors.push(`${where}: narrator に pose は付けられません`);
  }

  if (actions !== undefined) {
    if (!Array.isArray(actions)) {
      errors.push(`${where}: actions が配列ではありません`);
    } else {
      if (actions.length > QUIZ_LESSON.maxActions) {
        errors.push(
          `${where}: actions は${QUIZ_LESSON.maxActions}個までです(${actions.length}個)。` +
            `コマを分けてください`,
        );
      }
      for (const [i, action] of actions.entries()) {
        validateAction(action, slots, `${where}.actions[${i}]`, errors);
      }
    }
  }

  if (audio !== undefined) {
    if (!isText(audio)) errors.push(`${where}: audio が空です`);
    else if (!isQuizAssetPath(audio)) {
      errors.push(`${where}: audio は assets/ からの相対パスで書いてください: ${audio}`);
    }
  }
}

/**
 * 解説パート1本を検証してエラー一覧を返す(空なら OK)。
 *
 * @param value JSON をパースしただけの値(型は信用しない)
 * @param where エラーメッセージの位置表示(例 `lessons/sansu2-kuriagari.json`)
 * @param band  この解説が載る単元の帯(セリフの漢字の可否が変わる)
 */
export function validateQuizLesson(value: unknown, where: string, band?: QuizBand): string[] {
  const errors: string[] = [];
  if (!isRecord(value)) {
    errors.push(`${where}: オブジェクトではありません`);
    return errors;
  }
  const { id, title, board, frames } = value;

  if (!isText(id)) errors.push(`${where}: id が空です`);
  else if (!QUIZ_ID_PATTERN.test(id)) {
    errors.push(`${where}: id ${id} は英小文字+数字+ハイフンが必要です`);
  }

  // 見出しは単元名と同じ扱い(ふりがなを振る場所が無いので漢字は書けない)。
  if (!isText(title)) errors.push(`${where}: title が空です`);
  else if (QUIZ_KANJI.test(title)) errors.push(`${where}: title に漢字は使えません`);

  const slots = validateBoard(board, where, errors);

  if (!Array.isArray(frames)) {
    errors.push(`${where}: frames が配列ではありません`);
    return errors;
  }
  if (frames.length < QUIZ_LESSON.minFrames || frames.length > QUIZ_LESSON.maxFrames) {
    errors.push(
      `${where}: frames は${QUIZ_LESSON.minFrames}〜${QUIZ_LESSON.maxFrames}コマです` +
        `(${frames.length}コマ)`,
    );
  }
  for (const [i, frame] of frames.entries()) {
    validateFrame(frame, slots, band, `${where}.frames[${i}]`, errors);
  }

  return errors;
}

// ── 再生ヘルパー(描画側が使う)────────────────────────────

/** あるコマまで進めたときの盤面の状態。 */
export interface LessonBoardState {
  /** スロットID → いま入っている文字(`place` の積み上げ。`clear` で消える)。 */
  readonly values: ReadonlyMap<string, string>;
  /** そのコマだけ光っているスロット。 */
  readonly highlighted: ReadonlySet<string>;
  /** そのコマだけ出ている矢印。 */
  readonly arrows: readonly LessonAction[];
  /** そのコマだけ出ている囲み。 */
  readonly groups: readonly LessonAction[];
}

/**
 * `frames[0]` から `index` コマめまでを順に適用した盤面を作る。
 *
 * `place` / `clear` は**積み上げ**(前のコマの結果が残る)、
 * `highlight` / `arrow` / `group` は**そのコマだけ**の演出。
 * 純関数なので、途中のコマへ飛んでも・巻き戻しても同じ絵になる
 * (再生UIが「1コマ戻る」を持っても破綻しない)。
 */
export function lessonBoardStateAt(lesson: QuizLesson, index: number): LessonBoardState {
  const values = new Map<string, string>();
  const highlighted = new Set<string>();
  const arrows: LessonAction[] = [];
  const groups: LessonAction[] = [];

  const last = Math.min(index, lesson.frames.length - 1);
  for (let i = 0; i <= last; i += 1) {
    const frame = lesson.frames[i];
    if (!frame) continue;
    // 演出は「いま見せているコマ」のぶんだけ残す。
    if (i === last) {
      highlighted.clear();
      arrows.length = 0;
      groups.length = 0;
    }
    for (const action of frame.actions ?? []) {
      switch (action.kind) {
        case 'place':
          if (action.slot !== undefined && action.value !== undefined) {
            values.set(action.slot, action.value);
          }
          break;
        case 'clear':
          if (action.slots === undefined) values.clear();
          else for (const slot of action.slots) values.delete(slot);
          break;
        case 'highlight':
          if (i === last) for (const slot of action.slots ?? []) highlighted.add(slot);
          break;
        case 'arrow':
          if (i === last) arrows.push(action);
          break;
        default: // group
          if (i === last) groups.push(action);
          break;
      }
    }
  }

  return { values, highlighted, arrows, groups };
}

/** スロットを引く。 */
export function findLessonSlot(lesson: QuizLesson, slotId: string): LessonSlot | undefined {
  return lesson.board.slots.find((slot) => slot.id === slotId);
}

// ── 盤面の当たり判定・レイアウト計算 ────────────────────────
//
// 「どこに何を描くか」は盤面座標だけで決まる純粋な算数なので、描画側ではなく
// ここに置く(CLAUDE.md ルール1 を守ったまま、単体テストで数値を固定できる)。

/**
 * 盤面の外側にとる安全マージン(盤面座標)。
 *
 * スロットは**中心**で置くので、端のスロットは箱の半分が盤面の外に出る。
 * 枠線・囲みの点線・矢じりまで含めて必ず見えるように、描画範囲を外へ広げる。
 */
export const LESSON_BOARD_MARGIN = 5;

/**
 * 盤面の中の文字合わせのきまり(すべて盤面座標。px ではない)。
 *
 * 文字サイズを固定にすると「23ど」のような複数文字がますから飛び出す。
 * かといって小さすぎると小学生には読めない。**ますの内側に入る最大の大きさ**を
 * 文字数から計算し、入らないときだけ折り返す、という順番にしてある。
 */
export const LESSON_TEXT = {
  /** これ以上は大きくしない(数字1文字のますの見た目を今までどおりに保つ)。 */
  maxFontSize: 11,
  /** これ以上は小さくしない(小学生が読める下限)。 */
  minFontSize: 3,
  /** 行の高さ(フォントサイズ倍)。 */
  lineHeight: 1.15,
  /** 折り返してよい行数の上限。 */
  maxLines: 3,
  /**
   * 折り返しに乗り換える条件(何倍以上 大きく書けるようになるか)。
   * 「23ど」を「23 / ど」と積むと1割ほど大きく書けるが、読み手には1つの値なので
   * 少しの得では折らない。逆に「ながい もの」は倍ちかく変わるので折る。
   */
  wrapGain: 1.2,
  /** ますの内側の余白(枠線に文字がぶつからないように)。 */
  boxPadding: 1.3,
  /** 枠なし(`plain`)の余白。枠が無いぶん詰められる。 */
  plainPadding: 0.3,
  /** まるの中に取れる四角の一辺(直径に対する割合)。 */
  circleRatio: 0.75,
  /** そえがき(`arrow` / `group` の label)の大きさ。 */
  labelFontSize: 5,
  /** そえがきに使ってよい幅(盤面座標)。 */
  labelMaxWidth: 30,
  /** そえがきを線・囲みから持ち上げる高さ(盤面座標)。 */
  labelLift: 2,
  /** 横線だけの文字(分数の線)の字の高さ。ほぼ ink が無いので高さ制限を緩める。 */
  ruleInkRatio: 0.25,
} as const;

/** 四角(左上の座標と大きさ)。 */
export interface LessonRect {
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
}

/** スロットの外枠。`w` / `h` が省略されていれば `QUIZ_LESSON` の既定を使う。 */
export function lessonSlotBox(slot: LessonSlot): LessonRect {
  const w = slot.w ?? QUIZ_LESSON.slotWidth;
  const h = slot.h ?? QUIZ_LESSON.slotHeight;
  return { x: slot.x - w / 2, y: slot.y - h / 2, w, h };
}

/** `group` の囲み(スロット群のバウンディングボックス + 余白)。 */
export function lessonGroupBox(slots: readonly LessonSlot[], padding: number): LessonRect {
  const boxes = slots.map(lessonSlotBox);
  const x = Math.min(...boxes.map((box) => box.x)) - padding;
  const y = Math.min(...boxes.map((box) => box.y)) - padding;
  const right = Math.max(...boxes.map((box) => box.x + box.w)) + padding;
  const bottom = Math.max(...boxes.map((box) => box.y + box.h)) + padding;
  return { x, y, w: right - x, h: bottom - y };
}

/** 線分。 */
export interface LessonLine {
  readonly x1: number;
  readonly y1: number;
  readonly x2: number;
  readonly y2: number;
}

/**
 * 盤面全体の描画範囲(SVG の viewBox)。
 *
 * 抽象座標 0..`boardWidth` × 0..`boardHeight` を**そのまま** viewBox にすると、
 * 端に置いたスロットは枠線の太さのぶんだけ切れる(`x=0` のますは左半分が画面外)。
 * スロットは中心座標で置くので、`x` が範囲内でも**箱は範囲外**になり得るのも同じ理由。
 *
 * そこで「盤面 ∪ 全スロットの箱」を取り、その外へ `margin` を足したものを描画範囲にする。
 * これで囲み(`lessonGroupBox` の余白)や枠線を含めて必ず全部見える。
 * 1本の解説の中では**コマが変わっても範囲は動かない**(スロットは固定なので)。
 */
export function lessonBoardViewBox(
  board: LessonBoard,
  margin: number = LESSON_BOARD_MARGIN,
): LessonRect {
  let left = 0;
  let top = 0;
  let right: number = QUIZ_LESSON.boardWidth;
  let bottom: number = QUIZ_LESSON.boardHeight;
  for (const slot of board.slots) {
    const box = lessonSlotBox(slot);
    left = Math.min(left, box.x);
    top = Math.min(top, box.y);
    right = Math.max(right, box.x + box.w);
    bottom = Math.max(bottom, box.y + box.h);
  }
  return {
    x: left - margin,
    y: top - margin,
    w: right - left + margin * 2,
    h: bottom - top + margin * 2,
  };
}

/** 2つの四角が(`slack` より大きく)重なっているか。 */
export function lessonRectsOverlap(a: LessonRect, b: LessonRect, slack = 0): boolean {
  const x = Math.min(a.x + a.w, b.x + b.w) - Math.max(a.x, b.x);
  const y = Math.min(a.y + a.h, b.y + b.h) - Math.max(a.y, b.y);
  return x > slack && y > slack;
}

/** そえがきを置く場所(中心の y と、そこで許される大きさ)。 */
export interface LessonLabelSpot {
  readonly y: number;
  readonly maxFontSize: number;
}

/**
 * `group` のそえがきを置く場所。
 *
 * ふつうは囲みの**すぐ上**。ただし
 *   - 上に別のますが居る(文字が重なって両方読めなくなる)
 *   - 上が描画範囲からはみ出す(盤面の上端に近い囲み)
 * ときは、囲みの**内側の余白**(スロットとの間に空けてある `padding` の帯)へ逃がす。
 * 囲みは中身の外へ `padding` ぶん広げてあるので、この帯はいつでも空いている。
 *
 * @param obstacles 囲みに入っていないスロットの箱(重なりを避ける相手)
 * @param view      描画範囲(省略時ははみ出しを見ない)
 */
export function lessonGroupLabelSpot(
  box: LessonRect,
  padding: number,
  obstacles: readonly LessonRect[],
  view?: LessonRect,
): LessonLabelSpot {
  const font = LESSON_TEXT.labelFontSize;
  const above = box.y - LESSON_TEXT.labelLift;
  const rect: LessonRect = { x: box.x, y: above - font / 2, w: box.w, h: font };
  const outside = view !== undefined && rect.y < view.y + 0.2;
  const blocked = obstacles.some((other) => lessonRectsOverlap(rect, other, 0.2));
  if (!outside && !blocked) return { y: above, maxFontSize: font };
  // 内側の余白の帯(囲みの線とスロットの間)にちょうど収まる大きさにする。
  return { y: box.y + padding / 2, maxFontSize: Math.min(font, padding) };
}

/** 点を四角の中へ引き戻す(はみ出したそえがきを画面内に留める)。 */
export function clampLessonPoint(
  point: { readonly x: number; readonly y: number },
  view: LessonRect,
  insetX: number,
  insetY: number,
): { readonly x: number; readonly y: number } {
  const clamp = (value: number, min: number, max: number): number =>
    min > max ? (min + max) / 2 : Math.min(Math.max(value, min), max);
  return {
    x: clamp(point.x, view.x + insetX, view.x + view.w - insetX),
    y: clamp(point.y, view.y + insetY, view.y + view.h - insetY),
  };
}

/**
 * `arrow` の線分。中心どうしを結んだうえで、**両端をスロットの外へ逃がす**。
 * 逃がさないと矢じりが箱の下に潜って見えなくなる。
 */
export function lessonArrowLine(from: LessonSlot, to: LessonSlot, gap: number): LessonLine {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  // 同じ位置に重なったスロット(データの書き間違い)でも NaN を返さない。
  const length = Math.hypot(dx, dy) || 1;
  const ux = dx / length;
  const uy = dy / length;
  const fromBox = lessonSlotBox(from);
  const toBox = lessonSlotBox(to);
  const start = Math.max(fromBox.w, fromBox.h) / 2;
  const end = Math.max(toBox.w, toBox.h) / 2 + gap;
  return {
    x1: from.x + ux * start,
    y1: from.y + uy * start,
    x2: to.x - ux * end,
    y2: to.y - uy * end,
  };
}

// ── 盤面の文字合わせ ────────────────────────────────────────
//
// 「この文字列は このますに 何ポイントなら入るか」は、字幅の見積もりだけで決まる
// 純粋な算数なので、描画側ではなくここに置く(`ui/logic/textFit.ts` と同じ考えかた。
// あちらは画面 px、こちらは盤面座標)。実測ではなく見積もりなので、**はみ出す側には
// 倒れないよう少し大きめ**に幅を数える。

/** 全角として扱う文字(かな・漢字・全角記号)。 */
const LESSON_FULL_WIDTH = /[\u3000-\u30ff\u3400-\u4dbf\u4e00-\u9fff\uf900-\ufaff\uff00-\uff60]/;

/** 幅を持たない文字(異体字セレクタ・絵文字の連結子)。 */
const LESSON_ZERO_WIDTH = /[\ufe0e\ufe0f\u200d]/;

/** 横線だけでできた文字列(分数の線)。字の高さがほぼ無い。 */
const LESSON_RULE_ONLY = /^[―ー\-−–—_~〜]+$/;

/**
 * 1文字ぶんの字幅(em)。
 *
 * 日本語フォントの全角は 1em ちょうど。半角は字ごとに違うが、細い字を過小評価すると
 * はみ出すので**やや太めに**見積もる。絵文字は全角より少し広いフォントが多い。
 */
export function lessonCharWidthEm(ch: string): number {
  const code = ch.codePointAt(0) ?? 0;
  if (LESSON_ZERO_WIDTH.test(ch)) return 0;
  if (code >= 0x1f000) return 1.2; // 絵文字
  if (code >= 0x2190 && code <= 0x2bff) return 1; // 矢印・図形記号(⚪ ⛰ など)
  if (ch === '×' || ch === '÷' || ch === '±' || ch === '―' || ch === '〜') return 1;
  if (LESSON_FULL_WIDTH.test(ch)) return 1;
  if (ch === ' ') return 0.3;
  if (ch === '²' || ch === '³') return 0.5;
  if (ch >= '0' && ch <= '9') return 0.58;
  // 半角の字幅は `ui/logic/textFit.ts` と同じ見積もり(m / w は数字の1.5倍ちかく広い)。
  if (/[mwMW]/.test(ch)) return 0.88;
  if (/[iljI.,'!:;|]/.test(ch)) return 0.34;
  if (ch >= 'A' && ch <= 'Z') return 0.68;
  if (ch >= 'a' && ch <= 'z') return 0.58;
  return 0.55;
}

/**
 * 文字列を「1文字」の列に分ける。
 * 絵文字の異体字セレクタ・連結子は前の文字にくっつける(バラすと別の絵になる)。
 */
function lessonGraphemes(text: string): string[] {
  const out: string[] = [];
  let joining = false;
  for (const ch of text) {
    const last = out.length - 1;
    if (last >= 0 && (joining || LESSON_ZERO_WIDTH.test(ch))) {
      out[last] = (out[last] ?? '') + ch;
    } else {
      out.push(ch);
    }
    joining = ch === '\u200d';
  }
  return out;
}

/** 折返しの単位。`spaceBefore` は行頭に来たら消えるスペースの幅。 */
interface LessonToken {
  readonly text: string;
  readonly width: number;
  readonly spaceBefore: number;
}

/**
 * 折返し単位に分ける。
 * 日本語はどこでも折り返せるので全角1文字ずつ、半角の語はスペース区切りでひとかたまり
 * (`100cm` を `100` / `cm` に割らない)。
 */
function lessonTokens(text: string): LessonToken[] {
  const tokens: LessonToken[] = [];
  let pendingSpace = 0;
  let word = '';
  let wordWidth = 0;

  const flush = (): void => {
    if (word === '') return;
    tokens.push({ text: word, width: wordWidth, spaceBefore: pendingSpace });
    word = '';
    wordWidth = 0;
    pendingSpace = 0;
  };

  for (const ch of lessonGraphemes(text)) {
    if (ch === ' ' || ch === '　') {
      flush();
      pendingSpace += lessonCharWidthEm(ch);
      continue;
    }
    const width = lessonCharWidthEm(ch);
    if (width >= 0.9) {
      flush();
      tokens.push({ text: ch, width, spaceBefore: pendingSpace });
      pendingSpace = 0;
      continue;
    }
    word += ch;
    wordWidth += width;
  }
  flush();
  return tokens;
}

/** `tokens[i]` から `tokens[j]` までを1行に並べたときの幅(em)。 */
function lessonJoinWidth(tokens: readonly LessonToken[], i: number, j: number): number {
  let width = 0;
  for (let k = i; k <= j; k += 1) {
    const token = tokens[k];
    if (!token) continue;
    width += (k === i ? 0 : token.spaceBefore) + token.width;
  }
  return width;
}

/**
 * `lineCount` 行**以内**で、いちばん長い行がいちばん短くなる分けかたを探す。
 *
 * 貪欲に詰めると「ながい もの」が「ながい」/「もの」ではなく「ながい も」/「の」に
 * なりやすく、最長行が縮まない = 文字も小さくなる。行数が3までなので総当たりの
 * 動的計画法で足りる。
 */
function splitLessonLines(
  tokens: readonly LessonToken[],
  lineCount: number,
): { readonly lines: string[]; readonly width: number } | undefined {
  const n = tokens.length;
  if (n === 0 || lineCount < 1) return undefined;
  // best[l * (n+1) + i] = tokens[i..] を l 行以内に分けたときの「いちばん長い行」の最小値。
  // 添字を1本にして、抜けの無い平らな配列で持つ(3行 × 20語なので総当たりで足りる)。
  const stride = n + 1;
  const best = new Float64Array(stride * (lineCount + 1)).fill(Number.POSITIVE_INFINITY);
  const cut = new Int32Array(stride * (lineCount + 1)).fill(n - 1);
  for (let l = 0; l <= lineCount; l += 1) best[l * stride + n] = 0;

  for (let l = 1; l <= lineCount; l += 1) {
    for (let i = n - 1; i >= 0; i -= 1) {
      for (let j = i; j < n; j += 1) {
        // かな1文字だけの行は作らない(「ふん」を「ふ」「ん」と割るのは読みにくい)。
        // 半角の語(`16km` など)は1つで意味を持つので、1行になってもよい。
        if (n > 1 && j === i && (tokens[i]?.width ?? 0) >= 0.9) continue;
        const value = Math.max(lessonJoinWidth(tokens, i, j), best[(l - 1) * stride + j + 1] ?? 0);
        if (value < (best[l * stride + i] ?? 0)) {
          best[l * stride + i] = value;
          cut[l * stride + i] = j;
        }
      }
    }
  }

  const width = best[lineCount * stride] ?? Number.POSITIVE_INFINITY;
  if (!Number.isFinite(width)) return undefined;

  const lines: string[] = [];
  let i = 0;
  let l = lineCount;
  while (i < n && l > 0) {
    const j = cut[l * stride + i] ?? n - 1;
    let line = '';
    for (let k = i; k <= j; k += 1) {
      const token = tokens[k];
      if (!token) continue;
      line += (k === i || token.spaceBefore === 0 ? '' : ' ') + token.text;
    }
    lines.push(line);
    i = j + 1;
    l -= 1;
  }
  return { lines, width };
}

/** 文字合わせの結果。 */
export interface LessonTextFit {
  /** 使うフォントサイズ(盤面座標)。 */
  readonly fontSize: number;
  /** 折り返した行(1行なら要素1つ)。 */
  readonly lines: readonly string[];
  /** 行の高さ(盤面座標)。 */
  readonly lineHeight: number;
}

/** 文字合わせの調整つまみ(そえがき用に上限を下げるときに使う)。 */
export interface LessonTextFitOptions {
  readonly maxFontSize?: number;
  readonly minFontSize?: number;
  readonly maxLines?: number;
}

/**
 * 四角 `inner`(=**文字を置いてよい内側の範囲**)に収まる最大の文字サイズを選ぶ。
 *
 * 1行で入るならそのまま、入らないなら2行・3行と折り返して、いちばん大きく書ける
 * 組み合わせを採る。行数が同じ大きさになるときは**行数の少ないほう**を選ぶ。
 */
export function fitLessonText(
  text: string,
  inner: LessonRect,
  options: LessonTextFitOptions = {},
): LessonTextFit {
  const maxFontSize = options.maxFontSize ?? LESSON_TEXT.maxFontSize;
  const minFontSize = Math.min(options.minFontSize ?? LESSON_TEXT.minFontSize, maxFontSize);
  const tokens = lessonTokens(text);
  const maxLines = Math.max(1, Math.min(options.maxLines ?? LESSON_TEXT.maxLines, tokens.length));
  // 分数の線のように ink の高さがほぼ無い字は、高さで縮めると点になってしまう。
  const ink = LESSON_RULE_ONLY.test(text) ? LESSON_TEXT.ruleInkRatio : 1;

  let bestSize = -1;
  let bestLines: string[] = [text];
  for (let count = 1; count <= maxLines; count += 1) {
    const split = splitLessonLines(tokens, count);
    if (!split) continue;
    const rows = Math.max(split.lines.length, 1);
    const byWidth = inner.w / Math.max(split.width, 0.01);
    const byHeight = inner.h / (rows * LESSON_TEXT.lineHeight * ink);
    const size = Math.min(maxFontSize, byWidth, byHeight);
    // 行数の少ないほうから見ていき、はっきり大きく書けるときだけ折り返しに乗り換える。
    if (size > bestSize * LESSON_TEXT.wrapGain) {
      bestSize = size;
      bestLines = split.lines;
    }
  }

  // 切り上げるとその端数のぶんだけはみ出すので、必ず**切り捨て**で丸める。
  const fontSize = Math.floor(Math.max(bestSize, minFontSize) * 100) / 100;
  return {
    fontSize,
    lines: bestLines,
    lineHeight: Math.floor(fontSize * LESSON_TEXT.lineHeight * 100) / 100,
  };
}

/**
 * スロットの中で**文字を置いてよい範囲**。
 *
 * 枠のあるます(`box`)は枠線に文字がぶつからないよう内側へ、まる(`circle`)は
 * 直径に収まる四角へ、枠なし(`plain`)は地の文なのでほぼそのまま。
 */
export function lessonSlotTextBox(slot: LessonSlot): LessonRect {
  const box = lessonSlotBox(slot);
  const shape = slot.shape ?? 'box';
  if (shape === 'circle') {
    const side = Math.min(box.w, box.h) * LESSON_TEXT.circleRatio;
    return { x: slot.x - side / 2, y: slot.y - side / 2, w: side, h: side };
  }
  const pad = shape === 'plain' ? LESSON_TEXT.plainPadding : LESSON_TEXT.boxPadding;
  return {
    x: box.x + pad,
    y: box.y + pad,
    w: Math.max(box.w - pad * 2, 0.5),
    h: Math.max(box.h - pad * 2, 0.5),
  };
}

/** スロットに入る文字の大きさ(`lessonSlotTextBox` + `fitLessonText`)。 */
export function fitLessonSlotText(slot: LessonSlot, text: string): LessonTextFit {
  return fitLessonText(text, lessonSlotTextBox(slot));
}

// ── 盤面の分数(縦組み)────────────────────────────────────
//
// 盤面の値は `3/4` の1行書きで入っているが、算数の教科書は分数を**縦**に組む
// (分子・横線・分母)。`3/4` と平たく出すと「3わる4」に見えてしまう子がいるので、
// ますの中でも 2段+横線 にする。切り出しは `fraction.ts`(表示だけの純関数)、
// 「何ポイントで組めるか」の算数はここ。データ・読み上げ・判定は変えない。

/** 分数の組みかた(すべて em = フォントサイズ倍)。 */
export const LESSON_FRACTION = {
  /** 分子・分母の字を横線から離す高さ。 */
  gap: 0.22,
  /** 数字の字の高さの見積もり(数字はベースラインより下へ出ない)。 */
  digitHeight: 0.72,
  /** 横線を分子・分母より左右へ出す長さ(片側)。 */
  overhang: 0.12,
  /** 横線の太さ。 */
  rule: 0.08,
  /** 分数の左右にあける間(となりの字とくっつかないように)。 */
  side: 0.1,
} as const;

/** 分数を縦に組んだときの高さ(em)。上下対称なので片側の2倍。 */
export const LESSON_FRACTION_HEIGHT = (LESSON_FRACTION.gap + LESSON_FRACTION.digitHeight) * 2;

/** 組み上がった部品ひとつ(x は**中心からの相対**。盤面座標)。 */
export interface LessonFractionPart {
  readonly kind: 'text' | 'fraction';
  /** `text` のときの中身。 */
  readonly text?: string;
  /** `fraction` のときの分子(うえ)。 */
  readonly numerator?: string;
  /** `fraction` のときの分母(した)。 */
  readonly denominator?: string;
  /** 中心からの左端の位置。 */
  readonly x: number;
  /** 部品の幅。 */
  readonly w: number;
}

/** 分数を含む1行の組みかた(すべて盤面座標)。 */
export interface LessonFractionFit {
  readonly fontSize: number;
  readonly parts: readonly LessonFractionPart[];
  /**
   * 分子の**字の中心**(横線からの上へのずれ。負)。
   * 盤面の文字は `dominant-baseline: central`(中心そろえ)で置くので、
   * ベースラインではなく中心を返す。
   */
  readonly numeratorDy: number;
  /** 分母の**字の中心**(横線からの下へのずれ。正)。 */
  readonly denominatorDy: number;
  /** 横線の太さ。 */
  readonly ruleWidth: number;
  /** 全体の幅。 */
  readonly width: number;
}

/** 文字列の幅(em)。折返しはしないので単純な足し算。 */
function lessonRunWidthEm(text: string): number {
  let width = 0;
  for (const ch of lessonGraphemes(text)) width += lessonCharWidthEm(ch);
  return width;
}

/**
 * 分数トークンの幅(em)。
 *
 * 縦に積むので、横に要るのは**分子と分母の広いほう**だけ
 * (`3/4` は `3` `/` `4` の3文字ぶんではなく、1桁ぶんの幅で済む)。
 * 帯分数の整数部は分数の左に並ぶので、そのぶんを足す。
 */
export function lessonFractionWidthEm(run: FractionRun): number {
  const stack = Math.max(lessonRunWidthEm(run.numerator), lessonRunWidthEm(run.denominator));
  const whole = run.whole === undefined ? 0 : lessonRunWidthEm(`${run.whole}と`);
  return whole + stack + LESSON_FRACTION.overhang * 2 + LESSON_FRACTION.side * 2;
}

/**
 * 分数を含む文字列を**1行**に組む(ますの中で最大の大きさ)。
 *
 * 分数が1つも無ければ `undefined`(呼び出し側は今までどおり `fitLessonText` を使う)。
 * 分数のあるますの中身は `3/4` `1と2/5` のように短いので、折返しは考えない。
 */
export function fitLessonFractionText(
  text: string,
  inner: LessonRect,
  options: LessonTextFitOptions = {},
): LessonFractionFit | undefined {
  const runs = parseFractionRuns(text);
  if (!runs.some((run) => run.kind === 'fraction')) return undefined;

  const maxFontSize = options.maxFontSize ?? LESSON_TEXT.maxFontSize;
  const minFontSize = Math.min(options.minFontSize ?? LESSON_TEXT.minFontSize, maxFontSize);

  // 帯分数の整数部は「分数の左に置く文字」として、分数と同じ部品の中に持たせる。
  const items = runs.map((run) =>
    run.kind === 'text'
      ? { run, width: lessonRunWidthEm(run.text) }
      : { run, width: lessonFractionWidthEm(run) },
  );
  const totalEm = items.reduce((sum, item) => sum + item.width, 0);

  const byWidth = inner.w / Math.max(totalEm, 0.01);
  const byHeight = inner.h / LESSON_FRACTION_HEIGHT;
  // 切り上げるとその端数のぶんだけはみ出すので、必ず**切り捨て**で丸める。
  const fontSize =
    Math.floor(Math.max(Math.min(maxFontSize, byWidth, byHeight), minFontSize) * 100) / 100;

  const width = totalEm * fontSize;
  let x = -width / 2;
  const parts: LessonFractionPart[] = [];
  for (const item of items) {
    const w = item.width * fontSize;
    if (item.run.kind === 'text') {
      parts.push({ kind: 'text', text: item.run.text, x, w });
    } else {
      let at = x + LESSON_FRACTION.side * fontSize;
      let stack = w - LESSON_FRACTION.side * 2 * fontSize;
      if (item.run.whole !== undefined) {
        const wholeText = `${item.run.whole}と`;
        const wholeWidth = lessonRunWidthEm(wholeText) * fontSize;
        parts.push({ kind: 'text', text: wholeText, x: at, w: wholeWidth });
        at += wholeWidth;
        stack -= wholeWidth;
      }
      parts.push({
        kind: 'fraction',
        numerator: item.run.numerator,
        denominator: item.run.denominator,
        x: at,
        w: stack,
      });
    }
    x += w;
  }

  return {
    fontSize,
    parts,
    numeratorDy: -(LESSON_FRACTION.gap + LESSON_FRACTION.digitHeight / 2) * fontSize,
    denominatorDy: (LESSON_FRACTION.gap + LESSON_FRACTION.digitHeight / 2) * fontSize,
    ruleWidth: LESSON_FRACTION.rule * fontSize,
    width,
  };
}

/** スロットに入る分数の組みかた(分数が無ければ `undefined`)。 */
export function fitLessonSlotFraction(
  slot: LessonSlot,
  text: string,
): LessonFractionFit | undefined {
  return fitLessonFractionText(text, lessonSlotTextBox(slot));
}
